import { selectorFamily } from "recoil";

import { GROUPING_GRID_NO_GROUPS, NO_GROUPING_QS_VALUE } from "../../constants";
import {
  collectServerPaginatedCurrentPageRowIds,
  getTableFilteredGroupedRowsFromMap,
  isEntityServerSidePaginated,
  resolveBulkSelectionAllIds,
  resolveFirstNRowIdsFromVisibleGrid,
  sliceFirstRowIds,
  sliceRowIdsForPagination,
  sortTableRowsByColumnSorting,
  toTableItemPaginationGroupKey,
  unique,
} from "../../helpers";
import { RydeUiUnit } from "../../models";
import {
  bulkGridSelectionRowCountAF,
  bulkGridSelectionServerScopeIdsAF,
  tableColumnsSortingAF,
  tableGroupingAF,
  tableItemPaginationAF,
} from "../atoms";
import { boundedSelectorFamily } from "../selector-factories";
import {
  groupWithFilteredRowsSF,
  tableVisibleRowIdsForSelectAllSF,
} from "./table-filters.selectors";
import {
  tableFilteredGroupedRowsMapSF,
  tableGroupingFieldResolvedSF,
} from "./table-layout.selectors";

export const tableBulkSelectionAllIdsSF = boundedSelectorFamily<
  number[],
  RydeUiUnit
>({
  key: "tableBulkSelectionAllIdsSF",
  get:
    entity =>
    ({ get }) => {
      return resolveBulkSelectionAllIds({
        isServerPaginated: isEntityServerSidePaginated(entity),
        serverScopeIds: get(bulkGridSelectionServerScopeIdsAF(entity)),
        visibleRowIds: get(tableVisibleRowIdsForSelectAllSF(entity)),
      });
    },
});

export const tableBulkSelectionCurrentPageIdsSF = boundedSelectorFamily<
  number[],
  RydeUiUnit
>({
  key: "tableBulkSelectionCurrentPageIdsSF",
  get:
    entity =>
    ({ get }) => {
      const groupValues = get(groupWithFilteredRowsSF(entity));
      const groupedRowsMap = get(tableFilteredGroupedRowsMapSF(entity));
      const groupingFieldNameResolved = get(
        tableGroupingFieldResolvedSF(entity),
      );
      const sorting = get(tableColumnsSortingAF(entity));
      const columnGrouping = get(tableGroupingAF(entity));
      const isGrouped =
        columnGrouping?.length &&
        columnGrouping[0] !== undefined &&
        columnGrouping[0] !== NO_GROUPING_QS_VALUE;
      if (isEntityServerSidePaginated(entity)) {
        return unique(
          collectServerPaginatedCurrentPageRowIds({
            groupValues,
            groupedRowsMap,
            groupingFieldNameResolved,
          }),
        );
      }

      const allIds: number[] = [];

      for (const groupValue of groupValues) {
        const rows = getTableFilteredGroupedRowsFromMap({
          groupedRowsMap,
          groupingFieldValue: groupValue,
          groupingFieldNameResolved,
        });
        const sorted = sortTableRowsByColumnSorting({ rows, sorting });
        const paginationKey = toTableItemPaginationGroupKey(groupValue);
        const pagination = get(tableItemPaginationAF([paginationKey, entity]));
        const pageIds = sliceRowIdsForPagination({
          rows: sorted,
          pagination,
          isGroupedTable: Boolean(isGrouped),
        });

        allIds.push(...pageIds);
      }

      return unique(allIds);
    },
});

export const tableBulkSelectionFirstNIdsSF = selectorFamily<
  number[],
  [RydeUiUnit, number]
>({
  key: "tableBulkSelectionFirstNIdsSF",
  get:
    ([entity, count]) =>
    ({ get }) => {
      const groupedRowsMap = get(tableFilteredGroupedRowsMapSF(entity));
      const sorting = get(tableColumnsSortingAF(entity));
      const columnGrouping = get(tableGroupingAF(entity));
      const groupingFieldNameResolved = get(
        tableGroupingFieldResolvedSF(entity),
      );
      const isGrouped =
        columnGrouping?.length &&
        columnGrouping[0] !== undefined &&
        columnGrouping[0] !== NO_GROUPING_QS_VALUE;
      const groupValues = isGrouped
        ? get(groupWithFilteredRowsSF(entity))
        : [GROUPING_GRID_NO_GROUPS];

      const ids: number[] = [];
      let remaining = count;

      for (const groupValue of groupValues) {
        if (remaining <= 0) {
          break;
        }

        const rows = getTableFilteredGroupedRowsFromMap({
          groupedRowsMap,
          groupingFieldValue: groupValue,
          groupingFieldNameResolved,
        });
        const sorted = sortTableRowsByColumnSorting({ rows, sorting });
        const groupIds = sliceFirstRowIds({ rows: sorted, count: remaining });

        ids.push(...groupIds);
        remaining -= groupIds.length;
      }

      return ids;
    },
});

export const tableBulkSelectionRowCountIdsSF = boundedSelectorFamily<
  number[],
  RydeUiUnit
>({
  key: "tableBulkSelectionRowCountIdsSF",
  get:
    entity =>
    ({ get }) => {
      const count = get(bulkGridSelectionRowCountAF(entity));

      if (isEntityServerSidePaginated(entity)) {
        const serverScopeIds = get(bulkGridSelectionServerScopeIdsAF(entity));

        if (serverScopeIds.length) {
          return serverScopeIds;
        }

        const visibleRowIds = get(tableVisibleRowIdsForSelectAllSF(entity));
        const pageIndex =
          get(tableItemPaginationAF([GROUPING_GRID_NO_GROUPS, entity]))
            ?.pageIndex ?? 0;
        const fromVisible = resolveFirstNRowIdsFromVisibleGrid({
          visibleRowIds,
          count,
          pageIndex,
        });

        return fromVisible ?? [];
      }

      return get(tableBulkSelectionFirstNIdsSF([entity, count]));
    },
});
